import { Header } from "@made-people/blueprint-header";
import {
  Page,
  StoryblokStoryContentResponseV1,
} from "@made-people/nestjs-bff-service";
import { DesktopMenu } from "../features/menu/desktop-menu";
import { memo } from "react";
import { useRootLoaderData, gearsLoader } from "@made-people/gears-remix";
import { useRouteLoaderData } from "react-router";
import { mobileMenuOverlayId } from "../constants";
import {
  OldMenuProvider,
  useOldMenu,
} from "../features/old-menu/old-menu-context";
import { Topbar, topbarHasDarkBackground } from "./topbar";
import { SiteSettingsTriggerWithFlag } from "./site-settings-trigger-with-flag";
import { useAccountState } from "@made-people/grease-account";

const UserIconBold = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 18.05"
    stroke="currentColor"
    strokeWidth={0.5}
  >
    <path d="M8,11.05C1.4,11.05.2,15.85,0,18.05h1c.1-1.1.5-2.8,1.7-4.1,1.2-1.3,3-1.9,5.3-1.9s4.1.6,5.3,1.9c1.2,1.3,1.6,3,1.7,4.1h1c-.2-2.2-1.3-7-8-7Z" />
    <path d="M8,10.1c2.83,0,5.05-2.22,5.05-5.05S10.83,0,8,0,2.95,2.22,2.95,5.05s2.22,5.05,5.05,5.05ZM8,1c2.23,0,4.05,1.82,4.05,4.05s-1.78,4.05-4.05,4.05-4.05-1.78-4.05-4.05S5.77,1,8,1Z" />
  </svg>
);

const MissMaryHeaders = memo(
  (props: {
    isHomePage: boolean;
    pageType: Page["pageType"];
    globalStoryblokStories: StoryblokStoryContentResponseV1[];
  }) => {
    const { isOpen } = useOldMenu();
    const { customer } = useAccountState();

    const topbarTextClass = topbarHasDarkBackground(
      props.globalStoryblokStories,
    )
      ? "text-white"
      : "text-black";

    let color: "white" | "transparent" = "white";
    const isOverlay = props.isHomePage;
    if (props.isHomePage) {
      color = "transparent";
    }
    if (isOpen) {
      color = "white";
    }

    const isCheckout =
      props.pageType === "checkoutFinalize" ||
      props.pageType === "checkoutReceipt" ||
      props.pageType === "checkoutIndex";

    if (isCheckout) return null;

    return (
      <>
        <Header
          settings={{
            template: "template2",
            visibility: ["mobile", "tablet"],
            menuTrigger: {
              overlayId: mobileMenuOverlayId,
            },
            accountTrigger: {
              action: "drawer",
              href: "comfort-club",
              icon: {
                enabled: true,
                iconSvg: <UserIconBold />,
              },
              loggedIn: {
                isLoggedIn: !!customer,
                showInitials: true,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
              },
            },
            cartTrigger: {
              type: "link-to-checkout",
            },
            siteSettingsTrigger: {
              isEnabled: false,
            },
            topbar: (
              <Topbar globalStoryblokStories={props.globalStoryblokStories} />
            ),
            menu: (
              <DesktopMenu
                bgColor={color}
                globalStoryblokStories={props.globalStoryblokStories}
              />
            ),
          }}
        />

        <Header
          settings={{
            template: "barebone",
            visibility: "desktop",
            accountTrigger: {
              action: "drawer",
              href: "comfort-club",
              icon: {
                enabled: true,
                iconSvg: <UserIconBold />,
              },
              loggedIn: {
                isLoggedIn: !!customer,
                showInitials: true,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
              },
            },
            overlay: isOverlay,
            siteSettingsTrigger: {
              isEnabled: false,
            },
            variants: {
              color: color,
            },
            topbar: (
              <div className="relative">
                <Topbar globalStoryblokStories={props.globalStoryblokStories} />
                <div className="absolute first:hidden flex justify-end inset-0 max-w-component-max-w-xl box-content mx-auto px-component-spacing-xs md:px-component-spacing-md lg:px-component-spacing-md pointer-events-none">
                  <SiteSettingsTriggerWithFlag
                    className={`relative flex items-center gap-1 text-label-sm pointer-events-auto pt-1 ${topbarTextClass}`}
                  />
                </div>
              </div>
            ),
            menu: (
              <DesktopMenu
                bgColor={color}
                globalStoryblokStories={props.globalStoryblokStories}
              />
            ),
          }}
        />
      </>
    );
  },
);

MissMaryHeaders.displayName = "MissMaryHeaders";

/**
 * Wrapper that listen on rootLoaderData and passes it to the MissMaryHeaders component,
 * to minimize re-renders of the router component
 */
const Headers = memo(() => {
  const rootLoaderData = useRootLoaderData();
  const loaderData = useRouteLoaderData<typeof gearsLoader>(
    "project-router-wildcard",
  );

  if (!loaderData) {
    return null;
  }

  const {
    pageResponse: { page },
  } = loaderData;

  const pageType = page.pageType;
  const isHomePage =
    pageType === "storyblokPage" && page.storyblokData.slug === "site";

  const globalStoryblokStories =
    rootLoaderData?.settingsAndContentResponse.globalStoryblokStories ?? [];

  return (
    <OldMenuProvider>
      <MissMaryHeaders
        isHomePage={isHomePage}
        pageType={pageType}
        globalStoryblokStories={globalStoryblokStories}
      />
    </OldMenuProvider>
  );
});

Headers.displayName = "Headers";

export { Headers };
